import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/hooks/useAuth';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';

import Login from '@/routes/Login';
import Dashboard from '@/routes/Dashboard';
import ConfigRequired from '@/routes/ConfigRequired';
import Unauthorized from '@/routes/Unauthorized';
import NotFound from '@/routes/NotFound';

import Inventory from '@/routes/Inventory';
import Customers from '@/routes/Customers';
import CustomerDetail from '@/routes/CustomerDetail';
import Events from '@/routes/Events';
import ResetPassword from '@/routes/ResetPassword';
import EventStock from '@/routes/EventStock';
import EventDetail from '@/routes/EventDetail';
import POSGate from '@/components/pos/POSGate';
import Invoice from '@/routes/Invoice';
import PublicInvoice from '@/routes/PublicInvoice';
import Returns from '@/routes/Returns';
import Transactions from '@/routes/Transactions';
import Reports from '@/routes/Reports';
import Piutang from '@/routes/Piutang';
import Settings from '@/routes/Settings';
import Users from '@/routes/Users';
import AuditLogs from '@/routes/AuditLogs';
import Approvals from '@/routes/Approvals';

export default function App() {
  const location = useLocation();
  return (
    <AuthProvider>
      <ErrorBoundary resetKey={location.pathname}>
      <Routes>
        {/* Public — no auth required */}
        <Route path="/public-invoice/:id" element={<PublicInvoice />} />

        {/* Public / pre-auth */}
        <Route path="/login" element={<Login />} />
        {/* Public: the person following a recovery link is by definition not
            signed in, so this must sit outside the auth guard. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/config-required" element={<ConfigRequired />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Dedicated POS — full-screen, no AppShell, shift-gated. */}
        <Route
          path="/pos"
          element={
            <ProtectedRoute>
              <POSGate />
            </ProtectedRoute>
          }
        />
        {/* Step two of a sale: lots, promos, totals, then payment. */}
        <Route
          path="/pos/cart"
          element={
            <ProtectedRoute>
              <POSGate view="cart" />
            </ProtectedRoute>
          }
        />

        {/* Authenticated app shell (everything except POS) */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />

          {/* All roles */}
          <Route path="/invoice/:id" element={<Invoice />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/customers/:id" element={<CustomerDetail />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route path="/events/:eventId/stock" element={<EventStock />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/piutang" element={<Piutang />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/approvals" element={<Approvals />} />

          {/* Owner only */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute roles={['owner']}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute roles={['owner']}>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute roles={['owner']}>
                <AuditLogs />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      </ErrorBoundary>
    </AuthProvider>
  );
}
